import React, { useCallback, useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { ApiError, fetchWebChat, hasWebChat, sendWebChat, type WebChatMessage } from "@/lib/api";
import { getWebProfile, getWebSession } from "@/lib/webSession";

const READ_KEY = "kms.chatRead";
const readAt = (): string => {
  try {
    return localStorage.getItem(READ_KEY) ?? "";
  } catch {
    return "";
  }
};
const markRead = (iso: string) => {
  try {
    localStorage.setItem(READ_KEY, iso);
  } catch {
    /* ignore */
  }
};
const time = (iso: string, locale: string) => new Intl.DateTimeFormat(locale === "en" ? "en-GB" : "ru-RU", { hour: "2-digit", minute: "2-digit" }).format(new Date(iso));

const ICON = {
  chat: (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 5.5h16v10.5H9l-5 4z" />
      <path d="M8.5 10.5h.01M12 10.5h.01M15.5 10.5h.01" strokeWidth="2.4" />
    </svg>
  ),
  close: (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
      <path d="M6 9l6 6 6-6" />
    </svg>
  ),
  send: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M21.5 4.2 2.9 11.4c-1 .4-1 1.6.1 1.9l4.6 1.5 1.8 5.6c.2.8 1.2 1 1.8.4l2.6-2.4 4.8 3.5c.7.5 1.7.1 1.9-.8l3.2-15.2c.2-1-.8-1.8-1.7-1.5z" />
    </svg>
  ),
};

/**
 * "Спросить бариста" — the desktop website's chat. Messages go to the team's
 * support group in Telegram (one topic per visitor); answers come back here.
 */
export function BaristaChat() {
  const { t, locale } = useI18n();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<WebChatMessage[]>([]);
  const [online, setOnline] = useState(true);
  const [hours, setHours] = useState("");
  const [started, setStarted] = useState(hasWebChat());
  const [text, setText] = useState("");
  const [name, setName] = useState(getWebProfile()?.firstName ?? "");
  const [phone, setPhone] = useState("");
  const [trap, setTrap] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unread, setUnread] = useState(0);
  const body = useRef<HTMLDivElement>(null);
  const signed = !!getWebSession();
  const guestWithoutHistory = !signed && messages.length === 0;

  const merge = useCallback((list: WebChatMessage[]) => {
    if (!list.length) return;
    setMessages((prev) => {
      const seen = new Set(prev.map((m) => m.id));
      const next = [...prev, ...list.filter((m) => !seen.has(m.id))];
      return next.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    });
  }, []);

  const poll = useCallback(async () => {
    if (!started) return;
    try {
      const last = messages[messages.length - 1]?.createdAt;
      const res = await fetchWebChat(last);
      setOnline(res.online);
      setHours(res.hours);
      merge(res.messages);
    } catch {
      /* offline for a moment — try again on the next tick */
    }
  }, [started, messages, merge]);

  // First load (opening the widget) gets the status even without a conversation.
  useEffect(() => {
    if (!open) return;
    fetchWebChat()
      .then((res) => {
        setOnline(res.online);
        setHours(res.hours);
        if (res.chat) setStarted(true);
        merge(res.messages);
      })
      .catch(() => {});
  }, [open, merge]);

  // Polling: every 4 s while open, every 25 s in the background (for the unread badge).
  useEffect(() => {
    if (!started) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void poll();
    }, open ? 4000 : 25000);
    return () => window.clearInterval(id);
  }, [open, started, poll]);

  useEffect(() => {
    const lastStaff = [...messages].reverse().find((m) => m.fromStaff);
    if (open) {
      if (lastStaff) markRead(lastStaff.createdAt);
      setUnread(0);
    } else {
      const since = readAt();
      setUnread(messages.filter((m) => m.fromStaff && (!since || m.createdAt > since)).length);
    }
  }, [messages, open]);

  useEffect(() => {
    if (open) body.current?.scrollTo({ top: body.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    const openChat = () => setOpen(true);
    window.addEventListener("kms:chat", openChat);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("kms:chat", openChat);
    };
  }, []);

  const send = async (value?: string) => {
    const msg = (value ?? text).trim();
    if (!msg || sending) return;
    setSending(true);
    setError(null);
    try {
      const res = await sendWebChat({
        text: msg,
        name: name.trim() || undefined,
        phone: phone.trim() || undefined,
        page: window.location.pathname.slice(0, 200),
        website: trap,
      });
      setStarted(true);
      setOnline(res.online);
      merge([res.message]);
      if (value === undefined) setText("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error"));
    } finally {
      setSending(false);
    }
  };

  const quick = [t("desktop.chatQ1"), t("desktop.chatQ2"), t("desktop.chatQ3")];

  return (
    <>
      {open && (
        <section className="bc" role="dialog" aria-label={t("desktop.chatTitle")}>
          <header className="bc-h">
            <span className="bc-ava">
              <img src="/brand/kms-logo.png" alt="" />
              <i data-online={online} />
            </span>
            <div>
              <b>{t("desktop.chatTitle")}</b>
              <small>{online ? t("desktop.chatOnline") : t("desktop.chatOffline", { hours })}</small>
            </div>
            <button className="bc-x" onClick={() => setOpen(false)} aria-label={t("common.close")}>×</button>
          </header>
          <div className="bc-b" ref={body}>
            <div className="bc-m bc-m--them">
              {t("desktop.chatHello")}
            </div>
            {messages.map((m) => (
              <div key={m.id} className={`bc-m ${m.fromStaff ? "bc-m--them" : "bc-m--me"}`}>
                {m.fromStaff && m.author && <em>{m.author}</em>}
                {m.text}
                <small>{time(m.createdAt, locale)}</small>
              </div>
            ))}
            {messages.length > 0 && !messages[messages.length - 1].fromStaff && (
              <div className="bc-note">{online ? t("desktop.chatWait") : t("desktop.chatWaitOffline", { hours })}</div>
            )}
          </div>
          {messages.length === 0 && (
            <div className="bc-q">
              {quick.map((q) => (
                <button key={q} onClick={() => void send(q)} disabled={sending}>{q}</button>
              ))}
            </div>
          )}
          {guestWithoutHistory && (
            <div className="bc-who">
              <input value={name} onChange={(e) => setName(e.target.value.slice(0, 60))} placeholder={t("desktop.chatName")} autoComplete="name" />
              <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/[^\d+\s()-]/g, "").slice(0, 20))} placeholder={t("desktop.chatPhone")} inputMode="tel" autoComplete="tel" />
            </div>
          )}
          <input className="bc-hp" tabIndex={-1} autoComplete="off" value={trap} onChange={(e) => setTrap(e.target.value)} aria-hidden="true" />
          {error && <div className="bc-err">{error}</div>}
          <form
            className="bc-in"
            onSubmit={(e) => {
              e.preventDefault();
              void send();
            }}
          >
            <textarea
              rows={1}
              value={text}
              maxLength={1000}
              placeholder={t("desktop.chatPlaceholder")}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
            />
            <button type="submit" disabled={sending || !text.trim()} aria-label={t("desktop.chatSend")}>{ICON.send}</button>
          </form>
          <div className="bc-f">{signed ? t("desktop.chatFootSigned") : t("desktop.chatFootGuest")}</div>
        </section>
      )}
      <button className="bc-fab" data-open={open} onClick={() => setOpen((v) => !v)} aria-label={t("desktop.chatTitle")}>
        {open ? ICON.close : ICON.chat}
        {!open && <span className="bc-fab__label">{t("desktop.chatFab")}</span>}
        {!open && unread > 0 && <b>{unread}</b>}
      </button>
    </>
  );
}
